import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { IIntercomState } from './intercom-state';
import { BootIntercomAction, ShutdownIntercomAction } from './intercom-actions';
import { INTERCOM_BOOT, INTERCOM_SHUTDOWN } from '../action-types';
import { getCompany, getUser } from '../session/session-selectors';
import { catchError, map, of, retry, switchMap, tap, timeout, withLatestFrom } from 'rxjs';
import { Company } from '@core/models/common/company';
import { User } from '@core/models/user/user';
import { IntercomService } from '@vendor/intercom/intercom.service';
import { ClientConfigDataService } from '@core/data/api/config/client-config-data.service';

@Injectable()
export class IntercomEffects {

    constructor(private store$: Store<IIntercomState>,
                private actions$: Actions,
                private intercomService: IntercomService,
                private clientConfigDataService: ClientConfigDataService) {
    }

    /**
     * Boot intercom widget.
     *
     * Fetches the server-provided user_hash from /v3/client-config/identity
     * (5s timeout, up to 2 retries) and boots Intercom with identity
     * verification. On failure, boots without user_hash so the widget still
     * works — only identity verification is inactive.
     */
    bootIntercom$ = createEffect(() => this.actions$.pipe(
        ofType<BootIntercomAction>(INTERCOM_BOOT),
        withLatestFrom(
            this.store$.select(getUser),
            this.store$.select(getCompany)
        ),
        switchMap(([_, user, company]: [BootIntercomAction, User, Company]) =>
            this.clientConfigDataService.fetchIdentity().pipe(
                timeout(5000),
                map((identity) => identity?.intercom?.user_hash),
                retry(2),
                catchError((err) => {
                    console.warn(
                        '[intercom] Failed to fetch identity — booting without user_hash',
                        err,
                    );
                    return of(undefined);
                }),
                tap((userHash) => this.intercomService.boot(user, company, userHash)),
            )
        )
    ), { dispatch: false });

    /**
     * Shut down intercom widget
     */
    shutDownIntercom$ = createEffect(() => this.actions$.pipe(
        ofType<ShutdownIntercomAction>(INTERCOM_SHUTDOWN),
        tap((_) => {
            this.intercomService.shutDown();
        })
    ), { dispatch: false })
}
